import { MobileHeader } from './MobileHeader';
import { cva, type VariantProps } from 'class-variance-authority';
import { DesktopHeader } from './desktop/DesktopHeader';
import { DisplayMode } from '../../../types/enum/display-mode';
import type { SearchSuggestionResultGroup } from '../../search/view-models/search-suggestion';
import type { HeaderSearchTrackingConfig } from './HeaderSearch';
import type { CurrentCategoryType } from '../states/current-category-atom';

export const _headerVariants = cva(
    'duration-50 z-fixed w-full transition-all',
    {
        variants: {
            color: {
                default: 'bg-background text-primary', // default with dark text
                transparent: 'bg-transparent text-gray-50', // transparent with light text
                legacy: 'bg-[#FAF9F7]', // legacy background color
                none: 'bg-transparent text-primary', // transparent with dark text
                app: 'bg-white text-primary md:bg-[#F6F6F6]', // member app background color
                // B2B-aligned tokens (Figma 461:24583 / 521:28034). hero-overlay header only.
                b2bTransparent: 'bg-transparent text-white',
                b2bSolid: 'bg-white text-[#181818]',
                // Member-dashboard gray, mirrors the legacy `app` token so
                // /app routes don't sit on a white band over a gray page.
                b2bApp: 'bg-[#F6F6F6] text-[#181818]',
                // Mirrors the legacy `legacy` token (#FAF9F7) so legacy-chrome
                // pages (e.g. /shopping-cart) don't sit on a white band.
                b2bLegacy: 'bg-[#FAF9F7] text-[#181818]',
                // Transparent + dark text — mirrors the legacy `none` token
                // for routes that want to let the page background bleed
                // through (e.g. embedded login/booking flows). Safety net for
                // any route that declares `pageTheme: 'none'` without also
                // hiding the nav.
                b2bNone: 'bg-transparent text-[#181818]',
            },
            mode: {
                default: 'relative',
                fixed: 'fixed md:absolute',
                float: 'absolute',
            },
        },
        defaultVariants: {
            color: 'default',
            mode: 'default',
        },
    },
);

export interface HeaderCtaLink {
    label: string;
    href?: string;
    opensModal?: boolean;
}

interface HeaderCartItem {
    id: number;
    title: string;
    price: string;
    quantity: number;
    thumbnail: {
        desktop: string;
        mobile: string;
    };
    isSample: boolean;
}

export interface HeaderCartInfo {
    items: HeaderCartItem[];
    itemTotal: { ShortFormat: string; Amount: number };
    cartCount: number;
}

interface HeaderBaseProps extends VariantProps<typeof _headerVariants> {
    userData: HeaderUserData;
    cartInfo: HeaderCartInfo;
    currentUrl: string;
    onInboxClick?: () => void;
    onLogout: () => void;
    onLoginClick: () => void;
    onUserCartClick: () => void;
    warningMessages?: string[];
    onNavigate?: (url: string) => void;
    isLegacyBrokenRemMode?: boolean;
}

interface HeaderProps extends HeaderBaseProps {
    // Specific props for the main Header component
    currentCountryCode: string;
    allowMemberGuides?: boolean;
    allowMemberOrders?: boolean;
    countries: HeaderCountryElement[];
    primaryDesktopLinks: HeaderNavLinks;
    primaryMobileLinks: HeaderNavLinks;
    secondaryMobileLinks: HeaderNavLinks;
    profileMobileLinks: HeaderNavLinks;
    displayMode: DisplayMode;
    additionalUserMenuItems?: HeaderNavLinks;
    isSearching?: boolean;
    onMobileSearchClick?: () => void;
    productsPageTopnavDec2025Enabled?: boolean;
    fetchSuggestions?: (query: string) => void;
    suggestionData?: SearchSuggestionResultGroup | null;
    headerSearchTrackingConfig?: HeaderSearchTrackingConfig;
    showTextLogoOnly?: boolean;
    /**
     * Optional location object from Remix Router (pathname, search).
     * Provided when in Remix contexts; omit for non-Remix contexts.
     */
    location?: { pathname: string; search: string } | null;
    /** Small text above the link column (e.g. "For Suppliers & Brands"). */
    tagline?: string;
    /** Hero-overlay only: filled pill on the far right (e.g. "Apply to Join"). */
    primaryCta?: HeaderCtaLink;
    /** Hero-overlay only: text link to the left of the primary CTA (e.g. "Book a Demo"). */
    secondaryCta?: HeaderCtaLink;
    /**
     * Hero-overlay only: handler for `primaryCta` when `opensModal` is true (e.g. the B2B
     * lead modal). When omitted, the CTA falls back to its `href`.
     */
    onPrimaryCtaClick?: () => void;
    /**
     * Hero-overlay only: render the large "A" logomark below the header on the left.
     * Colour follows the variant (white on `b2bTransparent`, dark on `b2bSolid`).
     * When true, the header switches to its tall (378px) overlay layout.
     */
    showLogomark?: boolean;
    /**
     * v2 tall layout only: muted secondary nav items rendered below the primary
     * list (e.g. "Get Matched", "Request a Quote"). Renders in grey
     * (`text-[#9b9b9b]`) to read as lower-priority.
     */
    secondaryNavLinks?: HeaderNavLinks;
}

export interface HeaderCountryElement {
    code: string;
    name: string;
    url: string;
}

export interface HeaderCompanyInbox {
    id: number;
    count: number;
    inboxType?: string;
}

export interface HeaderUserData {
    isAuthorized: boolean;
    avatarSrc?: string;
    firstName: string;
    lastName?: string;
    notificationsCount: number;
    /** Pre-grouped professional sections ordered by country */
    professionalSections: ProfessionalSection[];
    /** Whether to show country labels (true when multiple countries exist) */
    showCountryLabels: boolean;
    inboxCount: number;
    designBoardCount: number;
    orderNotificationCount: number;
    websiteNotificationCount: number;
    isBuildHubEnabled: boolean;
}

/**
 * Represents a professional profile's essential data for display throughout the website.
 * This interface was exported to enable consistent professional representation across multiple components:
 * - Desktop user dropdown menu (professional account switcher in header)
 * - Mobile navigation menu (professional account switcher)
 * - Member dashboard sidebar navigation (logged-in homepage professional list)
 */
export interface HeaderUserDataProfessional {
    id: number;
    title: string;
    logo: string;
    companyLogo48Resized?: string;
    companyLogo96Resized?: string;
    logoBackgroundColor?: string;
    urlSegment: string;
    unreads: number;
    country?: string;
}

/**
 * Represents a section of professionals grouped by country
 */
export interface ProfessionalSection {
    country: string;
    label: string;
    professionals: HeaderUserDataProfessional[];
}

export interface HeaderNavLinks {
    title?: string;
    links: Array<{
        title: string;
        to: string;
        badgeCount?: number;
        onClick?: () => void;
        external?: boolean;
        isNonRemixRoute?: boolean;
        isSeparator?: boolean;
        prefix?: string;
        category?: CurrentCategoryType;
    }>;
}

export const Header = (props: HeaderProps) => {
    const {
        color,
        userData,
        currentUrl,
        onInboxClick,
        onLogout,
        onLoginClick,
        onUserCartClick,
        warningMessages,
        isLegacyBrokenRemMode,
        mode,
        currentCountryCode,
        allowMemberGuides,
        allowMemberOrders,
        countries,
        primaryDesktopLinks,
        primaryMobileLinks,
        secondaryMobileLinks,
        profileMobileLinks,
        displayMode,
        additionalUserMenuItems,
        onMobileSearchClick,
        cartInfo,
        tagline,
        primaryCta,
        secondaryCta,
        onPrimaryCtaClick,
        showLogomark,
        secondaryNavLinks,
    } = props;

    if (displayMode === DisplayMode.Desktop) {
        return (
            <DesktopHeader
                color={color}
                userData={userData}
                currentUrl={currentUrl}
                onInboxClick={onInboxClick}
                onLogout={onLogout}
                onLoginClick={onLoginClick}
                onUserCartClick={onUserCartClick}
                warningMessages={warningMessages}
                isLegacyBrokenRemMode={isLegacyBrokenRemMode}
                primaryLinks={primaryDesktopLinks}
                currentCountryCode={currentCountryCode}
                allowMemberGuides={allowMemberGuides}
                allowMemberOrders={allowMemberOrders}
                countries={countries}
                additionalUserMenuItems={additionalUserMenuItems}
                cartInfo={cartInfo}
                tagline={tagline}
                primaryCta={primaryCta}
                secondaryCta={secondaryCta}
                onPrimaryCtaClick={onPrimaryCtaClick}
                showLogomark={showLogomark}
                secondaryNavLinks={secondaryNavLinks}
            />
        );
    }

    return (
        <MobileHeader
            // Base props
            color={color}
            userData={userData}
            currentUrl={currentUrl}
            onLogout={onLogout}
            onLoginClick={onLoginClick}
            onUserCartClick={onUserCartClick}
            warningMessages={warningMessages}
            isLegacyBrokenRemMode={isLegacyBrokenRemMode}
            // Specific props
            mode={mode}
            primaryLinks={primaryMobileLinks}
            secondaryLinks={secondaryMobileLinks}
            profileLinks={profileMobileLinks}
            onMobileSearchClick={onMobileSearchClick}
            cartInfo={cartInfo}
            currentCountryCode={currentCountryCode}
            tagline={tagline}
        />
    );
};
